import React, { createContext, useContext, useState, useEffect } from "react";
import ApiService from "../services/ApiService";
import { toast } from "react-toastify";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userType, setUserType] = useState(null); // "talent" or "company"
  const [profileData, setProfileData] = useState(null);
  const [skillsData, setSkillsData] = useState(null); // only talent
  const [notifications, setNotifications] = useState({ notifications: [], pagination: { total_pages: 1, per_page: 10, total_records: 0 } });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  useEffect(() => {
    const companyData = localStorage.getItem("company");
    const talentData = localStorage.getItem("user");

    if (companyData) setUserType("company");
    else if (talentData) setUserType("talent");
    else setUserType(null);
  }, []);

  useEffect(() => {
    if (!userType) return;
    fetchProfile();
    fetchNotifications(1);
  }, [userType]);

  const fetchProfile = async () => {
    if (!userType) return;
    try {
      setLoadingProfile(true);
      const url = userType === "company" ? "/company/getProfile" : "getProfile";
      const res = await ApiService.request({ method: "GET", url });
      if (res.data.status) {
        const data = userType === "company" ? res.data.data.company : res.data.data;
        setProfileData(data);
        if (userType === "talent") fetchSkills();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Fetch Profile Error:", err);
      toast.error("Failed to fetch profile");
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchSkills = async () => {
    if (userType !== "talent") return;
    try {
      const res = await ApiService.request({ method: "GET", url: "getSkills" });
      if (res.data.status) setSkillsData(res.data.data.skills);
    } catch (err) {
      console.error("Fetch Skills Error:", err);
    }
  };

  const updateProfile = async (formData) => {
    if (!userType) return;
    try {
      const url = userType === "company" ? "/company/updateProfile" : "updateProfile";
      const res = await ApiService.request({
        method: "POST",
        url,
        data: formData,
        headers: userType === "company" ? { "Content-Type": "multipart/form-data" } : {},
      });
      if (res.data.status) {
        toast.success("Profile updated successfully!");
        fetchProfile();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Update Profile Error:", err);
      toast.error("Failed to update profile");
    }
  };

  // ==================== Notifications ====================

  const fetchNotifications = async (page = 1) => {
    if (!userType) return;
    try {
      setLoadingNotifications(true);
      const url =
        userType === "company"
          ? `company/getNotifications?page=${page}`
          : `getNotifications?page=${page}`;
      const res = await ApiService.request({ method: "GET", url });

      if (res.data.status) {
        setNotifications({
          notifications: res.data.data.notifications || [],
          unread_count: res.data.data.unread_count,
          pagination: res.data.data.pagination || { total_pages: 1, per_page: 10, total_records: 0 },
        });
        return res.data.data;
      } else {
        toast.error(res.data.message);
        return null;
      }
    } catch (err) {
      console.error("Fetch Notifications Error:", err);
      toast.error("Failed to fetch notifications");
      return null;
    } finally {
      setLoadingNotifications(false);
    }
  };

  const clearNotifications = async () => {
    if (!userType) return;
    try {
      const url =
        userType === "company"
          ? "company/clearAllNotifications"
          : "clearAllNotifications";
      const res = await ApiService.request({ method: "POST", url });
      if (res.data.status) {
        toast.success("Notifications cleared!");
        fetchNotifications(1);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Clear Notifications Error:", err);
      toast.error("Failed to clear notifications");
    }
  };

  const markNotificationAsRead = async (notificationId, page = 1) => {
    if (!userType) return;
    try {
      const formData = new FormData();
      formData.append("notification_id", notificationId);

      const url =
        userType === "company"
          ? "/company/readNotification"
          : "/readNotification";

      const res = await ApiService.request({
        method: "POST",
        url,
        data: formData,
      });

      if (res.data.status) {
        // refetch current page after marking as read
        fetchNotifications(page);
      } else {
        toast.error("Failed to mark notification as read");
      }
    } catch (err) {
      console.error("Mark Notification Read Error:", err);
      toast.error("Error marking notification as read");
    }
  };

  const readAllNotifications = async () => {
    if (!userType) return;
    try {
      const url =
        userType === "company"
          ? "company/readAllNotifications"
          : "readAllNotifications";
      
      const res = await ApiService.request({ method: "POST", url });
      if (res.data.status) {
        toast.success("All notifications marked as read!");
        fetchNotifications(1);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Read All Notifications Error:", err);
      toast.error("Failed to mark all as read");
    }
  };

  return (
    <UserContext.Provider
      value={{
        userType,
        profileData,
        skillsData,
        notifications,
        loadingProfile,
        loadingNotifications,
        fetchProfile,
        updateProfile,
        fetchNotifications,
        clearNotifications,
        markNotificationAsRead,
        readAllNotifications,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
